import { CircularProgress, useTheme } from '@material-ui/core';
import {
  Backdrop,
  CameraIconTwoTone,
  Typography,
  useConfirmationModal,
  useNotificationModal,
} from '@room-match/shared-ui-components';
import AsyncPhotoEditorV2 from 'components/AsyncPhotoEditorV2/AsyncPhotoEditorV2';
import AvatarSkeleton from 'components/Avatar/AvatarSkeleton/AvatarSkeleton';
import PublicProfile from 'pages/PublicProfile/PublicProfile';
import { Fragment, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import ProgressBar from 'shared/components/ProfileProgressBar/ProgressBar';
import { ROUTES } from 'shared/constants/ROUTES';
import StyledDialog from 'shared/elements/StyledDialog/StyledDialog';
import { useAgencyTalentPermission } from 'shared/hooks/useAgencyTalentPermission';
import useImageLoader from 'shared/hooks/useImageLoader';
import { IOutput, IProfileGraphQL, IProfileMediaSetSelectPayload } from 'shared/interfaces/IProfile';
import { ISkillCategory } from 'shared/interfaces/ISkills';
import { IOnProgress } from 'shared/interfaces/utils/IAxios';
import { useAppSelector } from 'shared/redux/store';
import { mediaService } from 'shared/services/mediaService';
import { profileService } from 'shared/services/profileService';
import { talentService } from 'shared/services/talentService';
import { uploadService } from 'shared/services/uploadService';
import { errorResponseToArray } from 'shared/utils/room-shared';
import { useDebounce } from 'use-debounce';
import SkillsData from '../../../data/Skills.json';
import user from '../user.png';
import {
  AgencyContact,
  AgencyName,
  Avatar,
  AvatarCont,
  ButtonCont,
  CameraCont,
  Completion,
  InfoCont,
  Name,
  SideButton,
  SideInfoCont,
} from './EditProfile.styles';
import { ProgressOverlay, useStyles } from './Tabs/ImagesVideoAudio/Items/item.styles';
import AddModal from './Tabs/ImagesVideoAudio/Modals/AddModal';
import { COLORS } from 'shared/constants/COLORS';
const {
  PASS_CIRCULAR_PROGRESS_BAR,
  WARNING_CIRCULAR_PROGRESS_BAR,
  ERROR_CIRCULAR_PROGRESS_BAR,
  ERROR_TEXT,
  DARK_CIRCULAR_PROGRESS_BAR_BACKGROUND,
  GRAY_CIRCULAR_PROGRESS_BACKGROUND,
} = COLORS;

const { getProfilePrimaryImage, setSelectProfileMedia, getSingleProfileGraphQL, setProfilePrimaryImage } =
  profileService();
const { getMediaList, getMediaTags } = mediaService();
const { archiveProfile } = profileService();
const { patchMultipleMedia, patchMediaTags, uploadMedia } = uploadService();
const { updateTalent, getSkills } = talentService();

type SideInfoProps = {
  data: IProfileGraphQL;
  permissions: string[];
};

function SideInfo({ data, permissions }: SideInfoProps) {
  const { user: userState } = useAppSelector((state) => state);

  const [showing, setshowing] = useState(false);
  const { data: skillsData } = getSkills();
  const { mutateAsync: updateSkillAsync } = updateTalent();
  const theme = useTheme();
  const [queryValue] = useDebounce('', 500);
  const { data: mediaData } = getMediaList({ file_type: 'image', media_bin: true });
  const { mutateAsync: patchMultipleMediaAsync } = patchMultipleMedia();
  const { mutateAsync: patchMediaTagsAsync } = patchMediaTags();
  const { mutateAsync: setSelectProfileMediaMutateAsync } = setSelectProfileMedia();
  const { data: mediaTags, isLoading: fetchingTags } = getMediaTags(queryValue);
  const queryClient = useQueryClient();
  const { handleOpen, isOpen, ConfirmationModal, handleSetMessage, handleChangeModalType } = useConfirmationModal({
    type: 'warning',
  });
  const { isNotifOpen, NotificationModal, handleAgencyTalentPermissions } = useAgencyTalentPermission(
    permissions,
    data.representation_type === 'non_represented',
  );
  const {
    handleOpen: notificationOpen,
    isOpen: isNotificationOpen,
    NotificationModal: NotifModal,
    handleSetMessage: notificationMessage,
  } = useNotificationModal({
    type: 'warning',
  });
  const { mutate, mutateAsync } = setSelectProfileMedia();
  const { mutate: primaryImageMutate, isLoading: isSaving } = setProfilePrimaryImage();
  const { mutate: archive, isLoading: isArchiving } = archiveProfile();
  const { mutateAsync: uploadMediaMutateAsync, isLoading: uploadMediaMutateLoading } = uploadMedia();
  const { profileId } = useParams() as { profileId: string };
  const [showEditor, setshowEditor] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const { data: imageData, isLoading: isLoadingPrimaryImage } = getProfilePrimaryImage(profileId);
  const { imageLoaded, noProfileImage, error } = useImageLoader(
    imageData?.data.attributes?.modified_attachment || imageData?.data?.attributes?.attachment || '',
  );
  const { data: profileData, isLoading: isMediaLoading } = getSingleProfileGraphQL(profileId);
  const [progress, setprogress] = useState(0);
  const classes = useStyles({});
  const [uploading, setUploading] = useState(false);
  const [viewProfile, setViewProfile] = useState(false);

  const history = useHistory();
  const queryclient = useQueryClient();
  const onProgres: IOnProgress = (progress: number) => {
    setprogress(progress);
  };

  const openEditor = (files: File[]) => {
    setFiles(files);
    setshowEditor(true);
  };

  useEffect(() => {
    const start = async () => {
      if (skillsData?.data.attributes.skills === null) {
        await updateSkillAsync({
          skills: SkillsData as ISkillCategory[],
        });
        await queryClient.invalidateQueries(['talents/skills']);
      }
    };
    start();
  }, [skillsData, updateSkillAsync, queryClient]);

  const handleMakePrimary = (mediaId: string) => {
    const formData = new FormData();
    formData.set('medium_id', mediaId);
    primaryImageMutate(
      { profileId, formData },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['profile', profileId]);
          queryClient.removeQueries('profiles');
          queryClient.removeQueries(['profile_primary_image', profileId]);
          queryClient.invalidateQueries(['profile_media', profileId, { file_type: 'image' }]);
          setUploading(false);
        },
      },
    );
  };

  const bindToProfile = async (payload: IProfileMediaSetSelectPayload) => {
    await mutateAsync(
      { profileId, payload: { ...payload, sort: 0 } },
      {
        onError: (errors) => {
          if (errors?.response?.data?.errors) {
            const errorResponseArray: any = errorResponseToArray(errors.response.data.errors);
            console.error('error', errorResponseArray.join(','));
          } else {
            console.error('error', 'Something went wrong');
          }
        },
      },
    );
    queryClient.invalidateQueries(['profile_media', profileId, { file_type: 'image' }]);
    queryClient.invalidateQueries(['media', { file_type: 'image' }], {
      refetchInactive: true,
    });

    handleMakePrimary(payload.id);
  };

  const handleOutput = async (image: IOutput) => {
    setUploading(true);
    const formData = new FormData();

    const {
      attachment,
      file_name,
      file_width,
      file_height,
      file_type,
      file_size,
      tag_list,
      modified_attachment,
      modified_metadata,
      attachment_size,
    } = image;
    formData.set('attachment', attachment);
    formData.set('modified_attachment', modified_attachment as any);
    formData.set('modified_metadata', modified_metadata as any);
    formData.set('attachment_size', attachment_size ? attachment_size.toString() : '');
    formData.append('tag_list', tag_list);
    formData.append('file_name', file_name);
    formData.append('file_type', file_type);
    formData.set('file_size', file_size);
    formData.set('file_height', file_height);
    formData.set('file_width', file_width);
    formData.append('tag_list', tag_list);
    formData.append('media_bin', 'true');

    const uploadedPhoto = await uploadMediaMutateAsync({ formData, onProgres });
    if (uploadedPhoto) {
      bindToProfile({
        id: uploadedPhoto.data.id,
        tag_list: 'image',
        media_tag_list: tag_list.split(','),
      });
    }
  };

  const progressOverlay = () => (
    <ProgressOverlay>
      {progress === 100 ? (
        <CircularProgress thickness={3} classes={{ circle: classes.circle }} />
      ) : (
        <>
          <CircularProgress thickness={3} variant="determinate" value={progress} classes={{ circle: classes.circle }} />
          <Typography style={{ position: 'absolute', fontSize: 12 }}>{`${Math.round(progress)}%`}</Typography>
        </>
      )}
    </ProgressOverlay>
  );

  const confirmDelete = () => {
    if (data.primary) {
      notificationOpen();
      notificationMessage('Warning', `You are not allowed to delete your primary representative`, 'OK');
    } else {
      handleChangeModalType('warning');
      handleSetMessage(
        'Delete Representative',
        `This will delete your representative. This action is irreversible. Are you sure you want to proceed?`,
      );
      handleOpen();
    }
  };

  const archivedProfile = () => {
    archive(profileId, {
      onSuccess: async () => {
        await queryClient.invalidateQueries('profiles');
        history.push(ROUTES.TALENT.PROFILES);
      },
    });
  };

  const handleAdd = () => {
    handleAgencyTalentPermissions({
      module: 'primary_image',
      callback: () => setAddModal(true),
      ctaText: 'OK',
    });
  };

  if (isArchiving) return <Backdrop isLoading />;

  return (
    <SideInfoCont>
      <AvatarCont>
        {uploading && progressOverlay()}
        {imageLoaded && !isLoadingPrimaryImage ? (
          <Avatar
            src={
              !isLoadingPrimaryImage && !error
                ? `${
                    imageData?.data.attributes.modified_attachment || imageData?.data.attributes.attachment || user
                  }/700x0?reinvalidate${new Date().getTime()}`
                : noProfileImage
            }
          />
        ) : (
          <AvatarSkeleton width={257} height={352} style={{ width: 257, height: 352, border: 'none', margin: 8 }} />
        )}
        <CameraCont className="camera-cont" onClick={handleAdd}>
          <CameraIconTwoTone />
        </CameraCont>
      </AvatarCont>
      <InfoCont>
        <Name>{data?.talent?.stage_name || data?.talent?.full_name}</Name>
        <AgencyName style={{ fontWeight: 'bold' }}>
          {data?.agency?.full_name || data.agency?.name || 'Non-Represented'}
        </AgencyName>
        {data?.division ? <AgencyName>{data?.division.name}</AgencyName> : null}
        {data?.representation_type !== 'non_represented' && (
          <>
            <AgencyContact>{data?.agency?.contact_number}</AgencyContact>{' '}
          </>
        )}
        <Completion
          style={{
            color:
              data.completeness < 50
                ? ERROR_CIRCULAR_PROGRESS_BAR
                : data.completeness === 100
                  ? PASS_CIRCULAR_PROGRESS_BAR
                  : WARNING_CIRCULAR_PROGRESS_BAR,
          }}
        >
          {data.completeness || 20}% Complete
        </Completion>
        <ProgressBar
          borderRadius={8}
          percent={data.completeness || 20}
          completeColor={
            data.completeness < 50
              ? ERROR_CIRCULAR_PROGRESS_BAR
              : data.completeness === 100
                ? PASS_CIRCULAR_PROGRESS_BAR
                : WARNING_CIRCULAR_PROGRESS_BAR
          }
          backgroundColor={
            theme.palette.type === 'dark' ? DARK_CIRCULAR_PROGRESS_BAR_BACKGROUND : GRAY_CIRCULAR_PROGRESS_BACKGROUND
          }
        />
      </InfoCont>
      <ButtonCont>
        <SideButton
          onClick={async () => {
            setshowing(true);
            await queryClient.invalidateQueries(['id', profileId], {
              refetchInactive: true,
            });

            setViewProfile(true);
            setshowing(false);
          }}
        >
          {showing ? <CircularProgress size={25} /> : <p style={{ fontSize: 16 }}>View Representative</p>}
        </SideButton>
        <SideButton onClick={confirmDelete} style={{ color: ERROR_TEXT }}>
          Delete Representative
        </SideButton>
      </ButtonCont>
      <StyledDialog
        style={{ zIndex: 9999 }}
        open={addModal}
        onClose={() => setAddModal(false)}
        maxWidth="md"
        aria-labelledby="simple-dialog-title"
      >
        <AddModal
          isProfile
          isPrimaryProfile={data.primary}
          close={() => setAddModal(false)}
          type="Images"
          openEditor={openEditor}
        />
      </StyledDialog>
      {showEditor && files && mediaTags && !fetchingTags && mediaData && !isMediaLoading && skillsData && (
        <>
          <AsyncPhotoEditorV2
            {...{
              patchMultipleMediaAsync,
              patchMediaTagsAsync,
              uploadMediaMutateAsync,
              setSelectProfileMediaMutateAsync,
              updateSkillAsync,
              setAsPrimary: true,
            }}
            variant="default"
            enableUndo={true}
            featureEnableRenderSkillTags={true}
            skillsData={skillsData.data}
            profileId={profileId}
            mediaData={mediaData.data}
            mediaTags={mediaTags.data}
            show={showEditor}
            images={files}
            close={() => {
              setshowEditor(false);
            }}
            onSuccessImageUpdate={() => {
              queryClient.invalidateQueries(['talents/skills']);
              queryClient.invalidateQueries(['profile_primary_image', profileId], {
                refetchInactive: true,
              });
            }}
            onSuccessImageUpload={(data: any) => {
              queryClient.invalidateQueries(['talents/skills']);
              queryClient.invalidateQueries(['profile_primary_image', profileId], {
                refetchInactive: true,
              });
              if (files.length === 1) {
                const formData = new FormData();
                formData.set('medium_id', data.data.id);
                primaryImageMutate(
                  { profileId: profileId, formData },
                  {
                    onSuccess: () => {
                      // After uploading ng image, nagiging overflow hidden ung whole body ng html,ang weird need ma-investigate to, for now ito muna solution
                      document.body.style.overflow = 'auto';
                      queryClient.invalidateQueries([
                        'profile_media',
                        profileId,
                        {
                          file_type: 'image',
                        },
                      ]);
                      queryClient.invalidateQueries(['profile_primary_image', profileId]);
                    },
                  },
                );
              }
            }}
          />
        </>
      )}
      {isOpen && <ConfirmationModal onConfirm={archivedProfile} confirmLabel="Delete" />}
      {isNotifOpen && <NotificationModal />}
      {isNotificationOpen && <NotifModal />}
      {viewProfile ? (
        <PublicProfile
          viewProfile={viewProfile}
          profileId={profileId}
          onClose={() => {
            setViewProfile(false);
          }}
        />
      ) : (
        <Fragment />
      )}
    </SideInfoCont>
  );
}

export default SideInfo;
